import { graphql, useFragment } from 'react-relay';
import { useEffect, useRef } from 'react';

import { getTrialDaysLeftFromTrialEndDate } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useTransitionId } from '@confluence/route-manager/entry-points/RouteState.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type { usePersistentInviteButtonEligibleLicenseDetailsQuery as usePersistentInviteButtonEligibleLicenseDetailsQuery$data } from './__types__/usePersistentInviteButtonEligibleLicenseDetailsQuery.ts';
import { PersistentInviteLicenseFragment } from './PersistentInviteEligibleQuery.graphql.ts';
import type { PersistentInviteEligibleQuery_license$key } from './__generated__/PersistentInviteEligibleQuery_license.graphql.ts';

export type PersistentInviteLicenseFragmentRef = PersistentInviteEligibleQuery_license$key;

type PersistentInviteEligibility = {
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	isEligible: boolean;
};

export function usePersistentInviteButtonEligible(): PersistentInviteEligibility {
	return usePersistentInviteButtonEligibleInternal();
}

export function usePersistentInviteButtonEligibleFromFragment({
	licenseFragmentRef,
}: {
	licenseFragmentRef: PersistentInviteLicenseFragmentRef;
}): PersistentInviteEligibility {
	const licenseData = useFragment(PersistentInviteLicenseFragment, licenseFragmentRef);

	return usePersistentInviteButtonEligibleInternal(licenseData);
}

function usePersistentInviteButtonEligibleInternal(
	routeLicenseData?: {
		readonly licenseConsumingUserCount: number | null;
		readonly trialEndDate: string | null;
	} | null,
): PersistentInviteEligibility {
	const { edition, isLicensed, isLoggedIn } = useSessionData();
	const hasLicensedAccess = useIsLicensed();

	const isFreeEdition = edition === ConfluenceEdition.FREE;
	const skipLicenseQuery =
		isFreeEdition ||
		!isLoggedIn ||
		(!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_license_error_fix')
			? !hasLicensedAccess
			: !isLicensed);
	const usesRouteData = routeLicenseData !== undefined;
	const {
		refetch,
		data: licenseDetailsData,
		loading: licenseDetailsLoading,
		error: licenseDetailsDataError,
	} = useQueryInterop<usePersistentInviteButtonEligibleLicenseDetailsQuery$data>(
		graphql`
			query usePersistentInviteButtonEligibleLicenseDetailsQuery {
				license {
					licenseConsumingUserCount
					trialEndDate
				}
			}
		`,
		{
			skip: skipLicenseQuery || usesRouteData,
		},
	);
	const hasChildQueryData = licenseDetailsData != null;
	const licenseData = hasChildQueryData ? licenseDetailsData.license : routeLicenseData;
	const trialDaysLeft = getTrialDaysLeftFromTrialEndDate(licenseData?.trialEndDate);

	const isInTrial =
		(skipLicenseQuery || licenseData?.licenseConsumingUserCount == null) &&
		!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_license_error_fix')
			? false
			: trialDaysLeft
				? trialDaysLeft > 0
				: false;

	// Sometimes, the trial end date is not set on the initial load for a new tenant, even though they have been put into a trial.
	// This can lead to the invite button not appearing. To mitigate this, we refetch the license details on page transition if we
	// suspect the user is in a trial, but the trial end date is null.
	const transitionId = useTransitionId();
	const hasRefetched = useRef(false);
	const initialTransitionId = useRef(transitionId);
	useEffect(() => {
		if (isInTrial || skipLicenseQuery || licenseDetailsLoading || licenseDetailsDataError) {
			return;
		}

		const trialSuspected = Boolean(
			licenseData?.trialEndDate === null &&
			licenseData?.licenseConsumingUserCount === 1 &&
			!isFreeEdition,
		);

		if (!hasRefetched.current && trialSuspected && transitionId > initialTransitionId.current) {
			void refetch();
			hasRefetched.current = true;
		}
	}, [
		edition,
		isFreeEdition,
		isInTrial,
		isLicensed,
		isLoggedIn,
		licenseData,
		licenseDetailsDataError,
		licenseDetailsLoading,
		refetch,
		skipLicenseQuery,
		transitionId,
	]);

	return {
		loading: usesRouteData && !hasChildQueryData ? false : licenseDetailsLoading,
		error: usesRouteData && !hasChildQueryData ? undefined : licenseDetailsDataError,
		isEligible: isInTrial || isFreeEdition,
	};
}
